import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://www.cloudlabweb.com";
const DEFAULT_OG = `${SITE_URL}/og-cover.jpg`;
const DEFAULT_TITLE = "CloudLab Technologies — AI Engineering & Software Development Company";
const DEFAULT_DESCRIPTION =
  "Award-winning AI engineering and custom software development company. Enterprise AI, campus ERPs, AI concierges, admissions CRMs and cloud platforms delivered across the UK, USA, Germany, KSA, UAE and Pakistan.";

/**
 * <Seo /> — per-page SEO block.
 *
 * Passes: title, description, keywords, image (og), type ("website" | "article" | "product" | "service"),
 * canonical (override the auto-computed path), jsonLd (object or array of extra JSON-LD blocks).
 */
export default function Seo({
  title,
  description,
  keywords,
  image,
  type = "website",
  canonical,
  jsonLd,
  noindex = false,
  robots,
}) {
  const loc = useLocation();
  const url = canonical || `${SITE_URL}${loc.pathname}`;
  const fullTitle = title ? `${title} — CloudLab Technologies` : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESCRIPTION;
  const ogImage = image || DEFAULT_OG;

  const ldArr = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      {robots ? (
        <meta name="robots" content={robots} />
      ) : noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      )}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="CloudLab Technologies" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@cloudlabweb" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {ldArr.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * Small helpers to build breadcrumb + schema JSON-LD.
 */
export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function serviceLd({ name, description, slug }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: name,
    name,
    description,
    provider: { "@id": `${SITE_URL}/#organization` },
    url: `${SITE_URL}/services/${slug}`,
    areaServed: [
      { "@type": "Country", "name": "United Kingdom" },
      { "@type": "Country", "name": "United States" },
      { "@type": "Country", "name": "Germany" },
      { "@type": "Country", "name": "Saudi Arabia" },
      { "@type": "Country", "name": "United Arab Emirates" },
      { "@type": "Country", "name": "Pakistan" },
    ],
  };
}

export function productLd({ name, description, slug, image, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    description,
    url: url || `${SITE_URL}/products/${slug}`,
    image,
    brand: { "@id": `${SITE_URL}/#organization` },
    publisher: { "@id": `${SITE_URL}/#organization` },
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web · iOS · Android",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };
}
